/**
 * Utility function to check doctor setup status
 * Returns an object with boolean values for each setup point
 * 
 * @param {string} userRole - The user's role ('doctor', 'patient', 'admin')
 * @param {Object} selectedDoctor - The doctor object from Redux state
 * @returns {Object|null} - Object with point1, point2, point3, point4 booleans, or null if not a doctor
 */
// Payment & Bank (Point 5) is complete when the doctor accepts at least one
// method and — if they accept ONLINE payments — has at least one bank account
// marked primary. A payout bank is only needed for online (the platform collects
// then pays out); Cash + Card/Terminal are collected in person on the doctor's own
// drawer/terminal, so they need no bank account.
// Reads doctorSettings.acceptedPaymentMethods + doctor.bankAccounts (both returned
// by getDoctorById).
export const isDoctorPaymentComplete = (selectedDoctor) => {
  const accepted = selectedDoctor?.doctorSettings?.acceptedPaymentMethods || [];
  if (!Array.isArray(accepted) || accepted.length === 0) return false;
  const requiresBank = accepted.includes('online');
  if (!requiresBank) return true;
  const banks = selectedDoctor?.bankAccounts || [];
  return Array.isArray(banks) && banks.some((b) => b && b.isPrimary);
};

export const getDoctorSetupStatus = (userRole, selectedDoctor) => {
  if (userRole !== 'doctor' || !selectedDoctor) {
    return null;
  }

  // Point 1: Profile Status is complete
  const isProfileComplete = selectedDoctor.profileStatus === 'complete';

  // Point 2: AppointmentSpeciality length > 0 AND AppointmentType length > 0
  const hasAppointmentSpeciality = selectedDoctor.appointmentSpeciality && 
                                   Array.isArray(selectedDoctor.appointmentSpeciality) && 
                                   selectedDoctor.appointmentSpeciality.length > 0;
  
  const hasAppointmentTypes = hasAppointmentSpeciality && 
                              selectedDoctor.appointmentSpeciality.some(spec => 
                                spec.appointmentTypes && 
                                Array.isArray(spec.appointmentTypes) && 
                                spec.appointmentTypes.length > 0
                              );

  // Point 3: Address length > 0
  const hasAddress = selectedDoctor.address && 
                     Array.isArray(selectedDoctor.address) && 
                     selectedDoctor.address.length > 0;

  // Point 4: Availability length > 0 AND has at least 1 object with active/isActive key true
  const availabilityArray = selectedDoctor.availability || [];
  const hasAvailability = Array.isArray(availabilityArray) && availabilityArray.length > 0;
  const activeDaysCount = availabilityArray.filter(avail => 
    avail.active === true || avail.isActive === true
  ).length;
  const has1ActiveDays = activeDaysCount >= 1;

  return {
    point1: isProfileComplete,
    point2: hasAppointmentSpeciality && hasAppointmentTypes,
    point3: hasAddress,
    point4: hasAvailability && has1ActiveDays,
    point5: isDoctorPaymentComplete(selectedDoctor),
  };
};

/**
 * Get missing setup items for doctor profile validation
 * Returns an array of missing item keys that can be used with translation function
 * 
 * @param {string} userRole - The user's role
 * @param {Object} selectedDoctor - The doctor object from Redux state
 * @returns {Array} - Array of missing item translation keys
 */
export const getDoctorMissingSetupItems = (userRole, selectedDoctor) => {
  if (userRole !== 'doctor' || !selectedDoctor) {
    return [];
  }

  const missingItems = [];

  // Check profile status (Point 1)
  const isProfileComplete = selectedDoctor.profileStatus === 'complete';
  if (!isProfileComplete) {
    missingItems.push('profileValidation.completeProfile');
  }

  // Check appointment speciality and types (Point 2)
  const hasAppointmentSpeciality = selectedDoctor.appointmentSpeciality && 
                                   Array.isArray(selectedDoctor.appointmentSpeciality) && 
                                   selectedDoctor.appointmentSpeciality.length > 0;
  
  const hasAppointmentTypes = hasAppointmentSpeciality && 
                              selectedDoctor.appointmentSpeciality.some(spec => 
                                spec.appointmentTypes && 
                                Array.isArray(spec.appointmentTypes) && 
                                spec.appointmentTypes.length > 0
                              );
  
  if (!hasAppointmentSpeciality || !hasAppointmentTypes) {
    missingItems.push('profileValidation.configureSpecialities');
  }

  // Check address (Point 3)
  const hasAddress = selectedDoctor.address && 
                     Array.isArray(selectedDoctor.address) && 
                     selectedDoctor.address.length > 0;
  if (!hasAddress) {
    missingItems.push('profileValidation.addAddress');
  }

  // Check availability (Point 4)
  const availabilityArray = selectedDoctor.availability || [];
  const hasAvailability = Array.isArray(availabilityArray) && availabilityArray.length > 0;
  const activeDaysCount = availabilityArray.filter(avail => 
    avail.active === true || avail.isActive === true
  ).length;
  const has1ActiveDays = activeDaysCount >= 1;
  
  if (!hasAvailability || !has1ActiveDays) {
    missingItems.push('profileValidation.setAvailability');
  }

  // Check payment & bank (Point 5)
  if (!isDoctorPaymentComplete(selectedDoctor)) {
    missingItems.push('profileValidation.setupPayment');
  }

  return missingItems;
};

